import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  IWithdrawalDetail,
  processWithdrawal,
} from "../../../services/affiliate";

interface WithdrawalsProps {
  withdrawals: IWithdrawalDetail[];
  username: string;
  userId: string;
}

const Withdrawals = ({ withdrawals, username, userId }: WithdrawalsProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    index: number,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(index);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleApproveClick = () => {
    setOpenApproveDialog(true);
    handleMenuClose();
  };

  const handleCancelClick = () => {
    setOpenCancelDialog(true);
    handleMenuClose();
  };

  const handleApproveConfirm = async () => {
    if (selectedRow === null) return;

    try {
      setProcessing(true);
      const withdrawalId = withdrawals[selectedRow]._id;
      await processWithdrawal(username, {
        withdraw_id: withdrawalId,
        status: "approve",
      });
      setMessage({ type: "success", text: "Withdrawal approved successfully" });
    } catch (error) {
      console.error("Error approving withdrawal:", error);
      setMessage({ type: "error", text: "Failed to approve withdrawal" });
    } finally {
      setProcessing(false);
      setOpenApproveDialog(false);
    }
  };

  const handleCancelConfirm = async () => {
    if (selectedRow === null) return;

    try {
      setProcessing(true);
      const withdrawalId = withdrawals[selectedRow]._id;
      await processWithdrawal(username, {
        withdraw_id: withdrawalId,
        status: "decline",
      });
      setMessage({ type: "success", text: "Withdrawal rejected successfully" });
    } catch (error) {
      console.error("Error rejecting withdrawal:", error);
      setMessage({ type: "error", text: "Failed to reject withdrawal" });
    } finally {
      setProcessing(false);
      setOpenCancelDialog(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "#F79009";
      case "paid":
      case "approved":
        return "#12B76A";
      case "failed":
      case "rejected":
        return "#F04438";
      default:
        return "#667185";
    }
  };

  // Format date and time
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    };
  };

  // Format amount
  const formatAmount = (amount: number) => {
    return `NGN ${amount.toLocaleString()}`;
  };

  // If no withdrawals data is available, show a message
  if (withdrawals.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography
          variant="h6"
          fontWeight="bold"
          gutterBottom
          sx={{ color: "#333", mb: 3 }}
        >
          Withdrawals
        </Typography>
        <Paper elevation={0} sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body1">
            No withdrawal requests found for this user.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {message.text && (
        <Alert
          severity={message.type as "success" | "error"}
          sx={{ mb: 2 }}
          onClose={() => setMessage({ type: "", text: "" })}
        >
          {message.text}
        </Alert>
      )}

      <Typography
        variant="h6"
        fontWeight="bold"
        gutterBottom
        sx={{ color: "#333", mb: 3 }}
      >
        Withdrawals
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <TextField
          placeholder="Search here..."
          variant="outlined"
          size="small"
          sx={{ width: "300px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            sx: {
              borderRadius: "8px",
            },
          }}
        />
        {/* <Box>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            sx={{ mr: 2, borderColor: "#344054", color: "#344054" }}
          >
            Filter
          </Button>
          <Button
            sx={{ mr: 2, borderColor: "#344054", color: "#344054" }}
            variant="outlined"
            startIcon={<CalendarTodayIcon />}
          >
            Select dates
          </Button>
        </Box> */}
      </Box>

      <Paper elevation={0} sx={{ maxWidth: "100%" }}>
        <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "#F0F2F5",
                  borderBottom: "1px solid #E4E7EC",
                }}
              >
                <TableCell sx={{ py: 1.5 }}>Date Requested</TableCell>
                <TableCell sx={{ py: 1.5 }}>Amount</TableCell>
                <TableCell sx={{ py: 1.5 }}>Bank Details</TableCell>
                <TableCell sx={{ py: 1.5 }}>Status</TableCell>
                <TableCell sx={{ py: 1.5 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {withdrawals.map((withdrawal, index) => {
                const { date, time } = formatDateTime(withdrawal.createdAt);
                return (
                  <TableRow key={withdrawal._id}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {date}
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{ mx: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {time}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{formatAmount(withdrawal.amount)}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: "10px", color: "#777" }}>
                        {withdrawal.bankName}
                      </Typography>
                      <Typography>{withdrawal.accountNumber}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          color: getStatusColor(withdrawal.status),
                          fontWeight: 500,
                        }}
                      >
                        {withdrawal.status.charAt(0).toUpperCase() +
                          withdrawal.status.slice(1)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        aria-label="more"
                        onClick={(e) => handleMenuClick(e, index)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedRow !== null &&
          withdrawals[selectedRow].status.toLowerCase() === "pending" && (
            <>
              <MenuItem onClick={handleApproveClick}>Approve</MenuItem>
              <MenuItem onClick={handleCancelClick}>Reject</MenuItem>
            </>
          )}
        {selectedRow !== null &&
          withdrawals[selectedRow].status.toLowerCase() !== "pending" && (
            <MenuItem onClick={handleMenuClose}>View Details</MenuItem>
          )}
      </Menu>

      {/* Approve Dialog */}
      <Dialog
        open={openApproveDialog}
        onClose={() => setOpenApproveDialog(false)}
      >
        <DialogTitle>Confirm Approval</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to approve this withdrawal request?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenApproveDialog(false)}>Cancel</Button>
          <Button
            onClick={handleApproveConfirm}
            color="success"
            variant="contained"
            disabled={processing}
          >
            {processing ? <CircularProgress size={24} /> : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog
        open={openCancelDialog}
        onClose={() => setOpenCancelDialog(false)}
      >
        <DialogTitle>Confirm Rejection</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reject this withdrawal request?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCancelDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCancelConfirm}
            color="error"
            variant="contained"
            disabled={processing}
          >
            {processing ? <CircularProgress size={24} /> : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Withdrawals;
